import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useAppContext } from '@/providers';
import { getJuthoorAdminInitiative } from '@/lib/api/juthoor-admin';
import { JuthoorAdminApproval } from '@/components/initiatives/juthoor-admin-approval';
import { JuthoorProfitLedgerPanel } from '@/components/initiatives/juthoor-profit-ledger';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { getStatusBadgeColor } from '@/lib/utils/initiative-helpers';
import { t } from '@/lib/i18n/messages';
import type { JuthoorInitiative } from '@/types/juthoor';

export default function JuthoorAdminInitiativeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { locale } = useAppContext();
  const isRTL = locale === 'ar';

  const [initiative, setInitiative] = useState<JuthoorInitiative | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInitiative = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getJuthoorAdminInitiative(id);
      if (response.success && response.data) {
        setInitiative(response.data);
      } else {
        setError(response.message || t(locale, 'error'));
      }
    } catch {
      setError(t(locale, 'error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitiative();
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4 md:p-8 flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner size="lg" label={t(locale, 'loadingData')} />
      </div>
    );
  }

  if (error || !initiative) {
    return (
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <ErrorMessage
          title={t(locale, 'error')}
          message={error || t(locale, 'noData')}
          onRetry={fetchInitiative}
          retryLabel={t(locale, 'retry')}
        />
      </div>
    );
  }

  const title = locale === 'ar' ? initiative.title_ar || initiative.title : initiative.title_en || initiative.title;
  const badgeColor = getStatusBadgeColor(initiative.status);
  const isPendingAdmin = initiative.status === 'pending_admin_approval';
  const isActive = ['active', 'planting_in_progress', 'completed'].includes(initiative.status);

  return (
    <div className={`max-w-4xl mx-auto p-4 md:p-8 space-y-6 pb-10 ${isRTL ? 'text-right' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Link
        to="/dashboard/admin/juthoor"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-primary transition-colors"
      >
        <span className="material-icons-outlined text-sm">arrow_back</span>
        {t(locale, 'juthoorAdminDashboard')}
      </Link>

      <div className="flex flex-wrap items-start gap-3">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex-1">{title}</h1>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${badgeColor}`}>
          {initiative.status.replace(/_/g, ' ')}
        </span>
      </div>

      {isPendingAdmin && (
        <JuthoorAdminApproval
          initiative={initiative}
          locale={locale}
          onApproveSuccess={fetchInitiative}
          onRejectSuccess={fetchInitiative}
        />
      )}

      {isActive && initiative.pricing && (
        <JuthoorProfitLedgerPanel initiativeId={initiative.id} locale={locale} />
      )}

      {!isPendingAdmin && !isActive && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isRTL ? 'لا توجد إجراءات متاحة لهذه الحالة.' : 'No actions available for this status.'}
          </p>
        </div>
      )}
    </div>
  );
}
